import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FundraisingFund, FUND_STAGES, FUND_PRIORITIES, FundStage, FundPriority } from '../../types';
import { Card, Button, Select, Input, Table, useTable, Checkbox } from "@repo/o37-ui";
import { Search, ExternalLink, MoreHorizontal } from "lucide-react";

interface FundLeadTableProps {
  funds: FundraisingFund[];
  onEditFund: (fund: FundraisingFund) => void;
  onStageChange: (fundId: string, newStage: string) => void;
}

type GroupingMode = 'none' | 'stage' | 'tier';

export default function FundLeadTable({ funds, onEditFund, onStageChange }: FundLeadTableProps) {
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<FundStage | 'all'>('all');
  const [priorityFilter, setPriorityFilter] = useState<FundPriority | 'all'>('all');
  const [showUninterested, setShowUninterested] = useState(false);

  // Determine grouping mode based on filters
  const groupingMode: GroupingMode = useMemo(() => {
    if (stageFilter !== 'all' && priorityFilter !== 'all') return 'none';
    if (stageFilter !== 'all' && priorityFilter === 'all') return 'tier';
    return 'stage'; // all stages + all tiers OR all stages + one tier
  }, [stageFilter, priorityFilter]);

  const filteredFunds = funds.filter(fund => {
    // Search filter
    const searchLower = search.toLowerCase();
    const matchesSearch = !search || 
      fund.name.toLowerCase().includes(searchLower) ||
      fund.contacts?.some(c => c.name.toLowerCase().includes(searchLower) || c.email?.toLowerCase().includes(searchLower));

    // Stage filter
    const matchesStage = stageFilter === 'all' || fund.stage === stageFilter;

    // Priority filter
    const matchesPriority = priorityFilter === 'all' || fund.priority === priorityFilter;

    // Uninterested filter
    const matchesUninterested = showUninterested || fund.stage !== 'uninterested';

    return matchesSearch && matchesStage && matchesPriority && matchesUninterested;
  });

  // Group funds based on grouping mode
  const groupedFunds = useMemo(() => {
    if (groupingMode === 'none') {
      return [{ key: 'all', label: '', funds: filteredFunds }];
    }
    
    if (groupingMode === 'stage') {
      const groups: { key: string; label: string; color: string; bgColor: string; funds: FundraisingFund[] }[] = [];
      FUND_STAGES.forEach(({ stage, label, color, bgColor }) => {
        const stageFunds = filteredFunds.filter(f => f.stage === stage);
        if (stageFunds.length > 0) {
          groups.push({ key: stage, label, color, bgColor, funds: stageFunds });
        }
      });
      // Handle uninterested if showing
      if (showUninterested) {
        const uninterestedFunds = filteredFunds.filter(f => f.stage === 'uninterested');
        if (uninterestedFunds.length > 0) {
          groups.push({ key: 'uninterested', label: 'Uninterested', color: 'text-neutral-500', bgColor: 'bg-neutral-50', funds: uninterestedFunds });
        }
      }
      return groups;
    }
    
    // groupingMode === 'tier'
    const groups: { key: string; label: string; color: string; bgColor: string; funds: FundraisingFund[] }[] = [];
    const tierBgColors: Record<FundPriority, string> = {
      'tier_1': 'bg-amber-50',
      'tier_2': 'bg-slate-100',
      'tier_3': 'bg-slate-50',
    };
    FUND_PRIORITIES.forEach(({ priority, label }) => {
      const tierFunds = filteredFunds.filter(f => f.priority === priority);
      if (tierFunds.length > 0) {
        groups.push({ key: priority, label, color: 'text-neutral-700', bgColor: tierBgColors[priority], funds: tierFunds });
      }
    });
    return groups;
  }, [filteredFunds, groupingMode, showUninterested]);

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const formatTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return `${Math.floor(days / 7)}w ago`;
  };

  const getStageConfig = (stage: FundStage) => 
    FUND_STAGES.find(s => s.stage === stage) || { label: stage, color: 'text-neutral-600', bgColor: 'bg-neutral-50' };

  const getPriorityConfig = (priority: FundPriority) =>
    FUND_PRIORITIES.find(p => p.priority === priority) || { label: priority, color: 'bg-neutral-400' };

  const columns = useMemo(() => [
    {
      id: "fund",
      header: "Fund",
      accessorFn: (row: any) => row.name,
      cell: ({ row }: any) => {
        const fund = row.original;
        return (
          <Link 
            to={`/fundraising/funds/${fund.id}`}
            className="flex items-center gap-3 group"
          >
            {fund.logoUrl ? (
              <img 
                src={fund.logoUrl} 
                alt={fund.name}
                className="w-8 h-8 rounded object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded bg-neutral-100 flex items-center justify-center text-xs font-medium text-neutral-500">
                {fund.name.charAt(0)}
              </div>
            )}
            <div>
              <div className="font-medium text-neutral-900 group-hover:underline decoration-neutral-400 underline-offset-2 decoration-dotted">{fund.name}</div>
              {fund.website && (
                <a 
                  href={fund.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs text-neutral-400 hover:text-blue-600 flex items-center gap-1"
                >
                  Website <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </Link>
        );
      }
    },
    {
      id: "primaryContact",
      header: "Primary Contact",
      cell: ({ row }: any) => {
        const fund = row.original;
        const primaryContact = fund.contacts?.find((c: any) => c.isPrimary) || fund.contacts?.[0];
        return primaryContact ? (
          <div>
            <div className="font-medium text-neutral-800">{primaryContact.name}</div>
            <div className="text-xs text-neutral-500">{primaryContact.title || primaryContact.email}</div>
          </div>
        ) : (
          <span className="text-neutral-400 text-xs italic">No contacts</span>
        );
      }
    },
    {
      id: "stage",
      header: "Stage",
      cell: ({ row }: any) => {
        const fund = row.original;
        const stageConfig = getStageConfig(fund.stage);
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Select
              value={fund.stage}
              onChange={(e) => onStageChange(fund.id, e.target.value)}
              className={`text-xs h-7 ${stageConfig.bgColor} border-0`}
            >
              {FUND_STAGES.map(({ stage, label }) => (
                <option key={stage} value={stage}>{label}</option>
              ))}
              <option value="uninterested">Uninterested</option>
            </Select>
          </div>
        );
      }
    },
    {
      id: "tier",
      header: () => <div className="text-center">Tier</div>,
      cell: ({ row }: any) => {
        const fund = row.original;
        const priorityConfig = getPriorityConfig(fund.priority);
        return (
          <div className="flex justify-center">
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium`}>
              <span className={`w-2 h-2 rounded-full ${priorityConfig.color}`} />
              {priorityConfig.label}
            </span>
          </div>
        );
      }
    },
    {
      id: "ticketSize",
      header: "Ticket Size",
      cell: ({ row }: any) => {
        const fund = row.original;
        if (fund.typicalTicketMin || fund.typicalTicketMax) {
          if (fund.typicalTicketMin && fund.typicalTicketMax) {
            return `${formatCurrency(fund.typicalTicketMin)} - ${formatCurrency(fund.typicalTicketMax)}`;
          } else if (fund.typicalTicketMin) {
            return `${formatCurrency(fund.typicalTicketMin)}+`;
          } else {
            return `Up to ${formatCurrency(fund.typicalTicketMax!)}`;
          }
        }
        return <span className="text-neutral-300">—</span>;
      }
    },
    {
      id: "updated",
      header: "Updated",
      cell: ({ row }: any) => (
        <span className="text-neutral-500 text-xs">
          {formatTimeAgo(row.original.updatedAt)}
        </span>
      )
    },
    {
      id: "actions",
      header: "",
      size: 50,
      cell: ({ row }: any) => (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            onClick={() => onEditFund(row.original)}
            className="h-7 w-7 p-0"
            icon={<MoreHorizontal className="w-4 h-4" />}
          />
        </div>
      )
    }
  ], [onStageChange, onEditFund]);

  return (
    <Card>
      {/* Filters */}
      <div className="p-4 border-b border-neutral-100">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              placeholder="Search funds or contacts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          
          <Select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value as FundStage | 'all')}
            className="w-28"
          >
            <option value="all">All Stages</option>
            {FUND_STAGES.map(({ stage, label }) => (
              <option key={stage} value={stage}>{label}</option>
            ))}
          </Select>

          <Select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as FundPriority | 'all')}
            className="w-24"
          >
            <option value="all">All Tiers</option>
            {FUND_PRIORITIES.map(({ priority, label }) => (
              <option key={priority} value={priority}>{label}</option>
            ))}
          </Select>

          <div className="flex items-center gap-2">
            <Checkbox 
              id="show-uninterested"
              checked={showUninterested} 
              onCheckedChange={(checked) => setShowUninterested(checked === true)} 
            />
            <label htmlFor="show-uninterested" className="text-sm text-neutral-600 cursor-pointer select-none">
              Show Uninterested
            </label>
          </div>

          <div className="text-sm text-neutral-500 whitespace-nowrap ml-auto pl-4">
            {filteredFunds.length} of {funds.length} funds
          </div>
        </div>
      </div>

      {/* Grouped Tables */}
      <div className="divide-y divide-neutral-100">
        {groupedFunds.map((group) => (
          <GroupedFundTable
            key={group.key}
            group={group}
            columns={columns}
            showHeader={groupingMode !== 'none'}
          />
        ))}
      </div>

      {filteredFunds.length === 0 && (
        <div className="p-8 text-center text-neutral-500">
          No funds match your filters
        </div>
      )}
    </Card>
  );
}

// Separate component for grouped table section
function GroupedFundTable({ 
  group, 
  columns, 
  showHeader 
}: { 
  group: { key: string; label: string; color?: string; bgColor?: string; funds: FundraisingFund[] };
  columns: any[];
  showHeader: boolean;
}) {
  const [localPagination, setLocalPagination] = useState({ pageIndex: 0, pageSize: 50 });
  
  const { table, ...tableProps } = useTable({
    data: group.funds,
    columns,
    pagination: localPagination,
    onPaginationChange: setLocalPagination,
    rowCount: group.funds.length,
    resourceName: (plural) => `fund${plural ? "s" : ""}`,
  });

  if (group.funds.length === 0) return null;

  return (
    <div>
      {showHeader && group.label && (
        <div className={`px-4 py-2.5 ${group.bgColor || 'bg-neutral-50'} border-b border-neutral-100`}>
          <div className="flex items-center justify-between">
            <span className={`text-xs font-semibold uppercase tracking-wider ${group.color || 'text-neutral-600'}`}>
              {group.label}
            </span>
            <span className="text-xs text-neutral-400">
              {group.funds.length} fund{group.funds.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}
      <Table {...tableProps} table={table} />
    </div>
  );
}
